
import { ContentIcon } from '@eliadmin/core/src/components/ui/content-icon';
import type { ReactNode } from 'react';

import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import type { PartnersHead } from './partners-types';

/*
 * Stavebné kamene stránky Pre partnerov. Hodnoty sú 1:1 z HTML návrhu (pre-partnerov.html, `.pp-*`).
 * Farba značky je `primary`, zlatá `accent` (obe z témy obchodu).
 */

export const WRAP = 'mx-auto w-full max-w-[1240px] px-5';
export const SECTION = 'py-[72px] min-[1024px]:py-[112px]';
export const SOFT = 'bg-[#F7F5F1]';
export const SAND = 'bg-[hsl(36_57%_88%)]';
export const DARK = 'bg-[#161412] text-white';
export const LINE = 'border border-solid border-[#161412]/10';

/*
 * Riadkovanie vždy ZA veľkosťou písma: tailwind-merge (cn) zahodí `leading-*`, za ktorým v tom
 * istom zozname nasleduje `text-[size]`.
 */
export const H1 =
  'm-0 text-[34px] font-bold leading-[1.1] tracking-[-.02em] min-[768px]:text-[50px] min-[1280px]:text-[60px]';
export const H2 =
  'm-0 text-[28px] font-bold leading-[1.1] tracking-[-.02em] min-[768px]:text-[38px]';
export const H3 = 'm-0 text-[20px] font-bold leading-[1.25] tracking-[-.02em]';

/** Odsek návrhu (`.pp p`): bez okrajov, riadkovanie 1.7 – vždy posledné. */
export function paragraph(...classes: Parameters<typeof cn>) {
  return cn('m-0', ...classes, 'leading-[1.7]');
}
export const MUTED = 'text-[#5f5c58]';
export const TAG =
  'self-start bg-accent px-[10px] py-[6px] text-[12px] font-bold uppercase tracking-[.08em] text-black';
export const BUTTON =
  'inline-flex h-[54px] cursor-pointer items-center gap-[10px] border-b-2 border-solid border-black px-[26px] text-[15px] font-semibold no-underline [transition:background_.2s,transform_.2s] hover:[transform:translateY(-1px)]';
export const BUTTON_BRAND = 'bg-primary text-white hover:bg-[hsl(27_52%_31%)]';
export const CARD = `flex flex-col gap-[14px] ${LINE} bg-white p-[30px]`;
/** Dva stĺpce: text a obrázok/karta (`.pp-media`). */
export const MEDIA =
  'grid grid-cols-1 items-center gap-10 min-[1000px]:grid-cols-[1.05fr_.95fr] min-[1000px]:gap-[72px] [&>*]:min-w-0';
export const COLUMN = 'flex flex-col gap-[22px]';
/** Odkaz v texte z administrácie (`.pp-link`). */
export const CMS_LINK =
  '[&_a]:font-semibold [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-[3px]';

/**
 * Text z rich-text editora administrácie. Obal nesie písmo a riadkovanie návrhu, vnútorné `<p>`
 * ho zdedia (inak by ich prebilo základné `p { text-base }`).
 */
export function CmsText({ html, className }: { html: string; className?: string }) {
  if (!html) return null;
  return (
    <HtmlRendererRaw
      html={html}
      className={paragraph('[&_p+p]:mt-3 [&_p]:m-0 [&_p]:[font:inherit]', className)}
    />
  );
}

export function Eyebrow({ children, dark }: { children: ReactNode; dark?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-[10px] text-[13px] font-bold uppercase tracking-[.12em] before:h-[2px] before:w-[28px] before:bg-current before:content-['']",
        dark ? 'text-accent' : 'text-primary',
      )}
    >
      {children}
    </span>
  );
}

export function Lead({ html, className }: { html: string; className?: string }) {
  return (
    <CmsText html={html} className={cn('max-w-[660px] text-[18px] text-[#4a4744]', className)} />
  );
}

/** Hlavička sekcie (`.pp-head`); `row` = nadpis vľavo, úvod vpravo od 1000 px. */
export function SectionHead({
  head,
  row,
  dark,
  className,
}: {
  head: PartnersHead;
  row?: boolean;
  dark?: boolean;
  className?: string;
}) {
  const title = (
    <>
      <Eyebrow dark={dark}>{head.eyebrow}</Eyebrow>
      <h2 className={cn(H2, dark && 'text-white')}>{head.title}</h2>
    </>
  );
  const lead = <Lead html={head.lead} className={cn(dark && 'text-white/[.76]')} />;

  return (
    <div
      className={cn(
        'mb-12 flex max-w-[760px] flex-col gap-4',
        row &&
          'max-w-none min-[1000px]:flex-row min-[1000px]:items-end min-[1000px]:justify-between min-[1000px]:gap-16',
        className,
      )}
    >
      {row ? (
        <div className="min-[1000px]:flex min-[1000px]:max-w-[640px] min-[1000px]:flex-col min-[1000px]:gap-4">
          {title}
        </div>
      ) : (
        title
      )}
      {lead}
    </div>
  );
}

export function CheckList({ items, dark }: { items: readonly string[]; dark?: boolean }) {
  if (!items.length) return null;
  return (
    <ul className="m-0 flex list-none flex-col gap-[10px] p-0">
      {items.map((item, index) => (
        <li key={index} className="flex gap-[10px] leading-[1.55]">
          <ContentIcon
            aria-hidden="true"
            className={cn(
              'fa-solid fa-check mt-[5px] text-[13px]',
              dark ? 'text-accent' : 'text-primary',
            )}
          />
          <HtmlRendererRaw as="span" html={item} />
        </li>
      ))}
    </ul>
  );
}
